import { alpha, Box, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useState } from 'react';

import { Identifier } from 'react-admin';
import { tableStyles } from '../../theme/customTheme';
import { BREAKPOINTS } from '../../theme/breakpoints';
import { CRM_COLORS, PAGE_MAX_WIDTH } from '../../theme/crmColors';
import '../../styles/list.css';
import { toFormatDate } from '../../utils/date';
import { Avatar, MCNote } from '../../utils/types';
import { ViewMode } from './ViewToggle';
import DataSection from './DataSection';
import TableView1 from './TableView1';
import CardsView1 from './CardsView1';
import MapAvatarsV2 from '../MapAvatarsv2/MapAvatarsV2';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface EnabledColumns {
    processingCapacity: boolean;
    sludgeStorageVolume: boolean;
    totalUsefulVolume: boolean;
    currentState: boolean;
    model: boolean;
}

const styles = () => ({
    ...tableStyles(),
    // Modern CRM styles
    modernContainer: {},
    // Converted from inline styles
    pageContainer: {
        display: 'grid',
        gap: 16,
        padding: 24,
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        boxSizing: 'border-box',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        minHeight: '100%',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            gap: 8,
            padding: 8,
        },
    },
    mapContainer: {
        display: 'flex',
        height: '100%',
        minHeight: 0,
    },
    iconSmall: {
        fontSize: 16,
    },
    helpText: {
        marginTop: '10px',
    },
    modernHeader: {
        marginBottom: '24px',
    },
    modernHeaderTitle: {
        fontSize: '2rem',
        fontWeight: 600,
        color: CRM_COLORS.text.primary,
        marginBottom: '8px',
    },
    modernHeaderSubtitle: {
        fontSize: '1rem',
        color: CRM_COLORS.text.secondary,
        marginBottom: '24px',
    },
    modernToolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px',
    },
    modernSearchBox: {
        minWidth: 300,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: CRM_COLORS.surface,
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: CRM_COLORS.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: CRM_COLORS.primary,
                borderWidth: 2,
            },
        },
    },
    modernActionButtons: {
        display: 'flex',
        gap: '8px',
    },
    modernActionButton: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
        padding: '8px 16px',
        boxShadow: CRM_COLORS.shadow,
        '&:hover': {
            boxShadow: CRM_COLORS.shadowHover,
        },
    },
    modernPrimaryButton: {
        backgroundColor: CRM_COLORS.primary,
        color: 'white',
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.primary, 0.9),
        },
    },
    modernSecondaryButton: {
        backgroundColor: CRM_COLORS.surface,
        color: CRM_COLORS.text.primary,
        border: `1px solid ${CRM_COLORS.border}`,
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.primary, 0.05),
        },
    },
    modernStatsCards: {
        display: 'flex',
        gap: '12px',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    modernStatCard: {
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: `1px solid ${alpha(CRM_COLORS.border, 0.6)}`,
        backgroundColor: 'white',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.primary, 0.02),
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            transform: 'translateY(-1px)',
        },
    },
    modernStatCardContent: {
        padding: '16px !important',
    },
    modernStatValue: {
        fontSize: '2rem',
        fontWeight: 700,
        color: CRM_COLORS.text.primary,
    },
    modernStatLabel: {
        fontSize: '0.875rem',
        color: CRM_COLORS.text.secondary,
        fontWeight: 500,
    },
    modernStatIcon: {
        width: 40,
        height: 40,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modernAncCard: {
        borderRadius: 12,
        boxShadow: CRM_COLORS.shadow,
        border: `1px solid ${CRM_COLORS.border}`,
        marginBottom: '16px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            boxShadow: CRM_COLORS.shadowHover,
            transform: 'translateY(-1px)',
        },
    },
    modernAncCardHeader: {
        padding: '20px',
        paddingBottom: '12px',
        borderBottom: `1px solid ${CRM_COLORS.border}`,
    },
    modernAncCardContent: {
        padding: '20px',
    },
    modernAncTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        color: CRM_COLORS.text.primary,
        marginBottom: '4px',
    },
    modernAncSubtitle: {
        fontSize: '0.875rem',
        color: CRM_COLORS.text.secondary,
        marginBottom: '8px',
    },
    modernAncStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
    },
    modernStatusChip: {
        borderRadius: 16,
        fontWeight: 500,
        fontSize: '0.75rem',
        height: 24,
    },
    modernStatusActive: {
        backgroundColor: alpha(CRM_COLORS.success, 0.1),
        color: CRM_COLORS.success,
        border: `1px solid ${alpha(CRM_COLORS.success, 0.3)}`,
    },
    modernStatusWarning: {
        backgroundColor: alpha(CRM_COLORS.warning, 0.1),
        color: CRM_COLORS.warning,
        border: `1px solid ${alpha(CRM_COLORS.warning, 0.3)}`,
    },
    modernStatusError: {
        backgroundColor: alpha(CRM_COLORS.error, 0.1),
        color: CRM_COLORS.error,
        border: `1px solid ${alpha(CRM_COLORS.error, 0.3)}`,
    },
    modernAncDetails: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '16px',
    },
    modernDetailItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    modernDetailLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: CRM_COLORS.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    modernDetailValue: {
        fontSize: '0.875rem',
        color: CRM_COLORS.text.primary,
        fontWeight: 500,
    },
    modernAncActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: `1px solid ${CRM_COLORS.border}`,
    },
    modernActionIcons: {
        display: 'flex',
        gap: '8px',
    },
    modernActionIcon: {
        width: 36,
        height: 36,
        borderRadius: 8,
        backgroundColor: alpha(CRM_COLORS.primary, 0.1),
        color: CRM_COLORS.primary,
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.primary, 0.2),
        },
    },
    modernLastUpdate: {
        fontSize: '0.75rem',
        color: CRM_COLORS.text.secondary,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    dialogContentRoot: {
        overflowX: 'hidden',
        padding: '0 20px 0px 20px !important',
    },
    dialogTitle: {
        padding: '17px 20px 0 20px',
        paddingBottom: 10,
    },
    container: {
        display: 'grid',
        gridTemplateColumns: '25px auto',
        alignItems: 'center',
        columnGap: '7px',
    },
    dialogTitleText: {
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontSize: 17,
    },
    fileWrapper: {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    },
    fileInput: {
        width: '100%',
    },
    filePreview: {
        marginTop: '15px',
        width: '100%',
    },
    circularProgress: {
        width: '16px !important',
        height: '16px !important',
        marginRight: '7px',
    },
    buttonsContainer: {
        marginTop: '20px',
        textAlign: 'right',
    },
    buttons: {
        '&:first-child': {
            marginRight: '7px',
        },
    },
    helperMessage: {
        fontWeight: 'bold',
        marginTop: '15px',
        marginBottom: '5px',
    },
});
//@ts-ignore
const useStyles = makeStyles(styles);

interface AvatarAncListProps {
    viewMode: ViewMode;
}

export const AvatarAncList = ({ viewMode }: AvatarAncListProps) => {
    const classes = useStyles();

    const isMobile = useIsMobile();
    // En móvil la tabla no se ofrece: cae a tarjetas. El mapa sí es responsive y se respeta.
    const effectiveViewMode = isMobile && viewMode === 'table' ? 'cards' : viewMode;

    const [enabledColumns, setEnabledColumns] = useState<EnabledColumns>({
        processingCapacity: false,
        sludgeStorageVolume: false,
        totalUsefulVolume: false,
        model: false,
        currentState: true,
    });

    const toggleColumn = (column: string, enabled: boolean) =>
        setEnabledColumns({
            ...enabledColumns,
            [column]: enabled,
        });

    return (
        <Box className={classes.pageContainer}>
            {/* Data Section with Collapse - Visible in both table and card views */}
            <DataSection />

            {effectiveViewMode === 'table' && <TableView1 enabledColumns={enabledColumns} toggleColumn={toggleColumn} />}

            {effectiveViewMode === 'cards' && <CardsView1 enabledColumns={enabledColumns} toggleColumn={toggleColumn} />}

            {effectiveViewMode === 'map' && (
                <Box className={classes.mapContainer}>
                    <MapAvatarsV2
                        mapHeight='100%'
                        initialLocation={{
                            latitude: 48.8589506,
                            longitude: 2.2768488,
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

interface DateFieldWrapperProp {
    record?: Avatar;
    notes?: {
        [key: Identifier]: MCNote;
    };
    headerClassName?: string;
}

const DateFieldWrapper = (props: DateFieldWrapperProp) => {
    const noteIds = 'notes' in props && typeof props.notes === 'object' ? Object.keys(props.notes) : [];
    const noteId = noteIds.length > 0 ? noteIds[0] : null;

    return <>{noteId && props.notes ? toFormatDate(props.notes[noteId].createdAt) : ''}</>;
};

DateFieldWrapper.propTypes = {
    record: PropTypes.object,
    notes: PropTypes.object,
};
